import { LEAVES_BG_XS, LEAVES_BG_MD, ROZSAK_BG } from '../config/backgrounds';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { trackProviderEvent, trackGallerySwipe, ANALYTICS_EVENTS } from '../services/analyticsService';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { hu } from 'date-fns/locale';
import { Helmet } from 'react-helmet-async';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Rating,
  Avatar,
  Pagination,
  CircularProgress,
  Alert,
  Paper,
  Stack,
  Divider,
  IconButton,
  Grow,
  Slide,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Slider,
  Collapse,
  Drawer,
  alpha,
  useMediaQuery
} from '@mui/material';
import { useTheme, styled, keyframes } from '@mui/material/styles';
import {
  Search,
  LocationOn,
  Phone,
  Email,
  Visibility,
  FilterList,
  Clear,
  Sort,
  Verified,
  CalendarToday,
  TrendingUp,
  Star as StarIcon,
  Business,
  PhotoCamera,
  Schedule,
  ContentCopy,
  Close,
  ArrowBackIos,
  ArrowForwardIos,
  EventAvailable,
  ZoomIn,
} from '@mui/icons-material';
import { useNavigate, useSearchParams, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import providerService from '../services/providerService';
import DialogHeader from '../components/Common/DialogHeader';
import categoryService from '../services/categoryService';
import OfferRequestForm from '../components/Booking/OfferRequestForm';
import FavoriteButton from '../components/Favorites/FavoriteButton';
import FeaturedBadge from '../components/Featured/FeaturedBadge';
import ProviderCard from '../components/Provider/ProviderCard';
import ImageLightbox from '../components/ImageGallery/ImageLightbox';
import SearchAutocomplete from '../components/Search/SearchAutocomplete';
import { useLayout } from '../contexts/LayoutContext';
import API_BASE_URL from '../config/api';
import { findCountyByParam } from '../constants/counties';
import { providersLandingPath, countyLabel } from '../utils/landingPaths';
import CategorySeoContent from '../components/SeoContent/CategorySeoContent';
import apiService from '../services/apiService';


// CountUp komponens marad, ahogy van
const CountUp = ({ end, duration = 2000, suffix = '', decimalPlaces = 0 }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.5 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!inView) return;

    let startTimestamp;
    const animate = (timestamp) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      const currentCount = progress * end;
      setCount(currentCount);
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setCount(end);
      }
    };

    requestAnimationFrame(animate);
  }, [end, duration, inView]);

  return <span ref={ref}>{count.toFixed(decimalPlaces)}{suffix}</span>;
};

// ÚJ: Statisztika megjelenítő Box komponens
const StatBox = ({ value, label }) => {
  const theme = useTheme();
  return (
    <Box sx={{ textAlign: 'center' }}>
      <Typography
        variant="h3"
        sx={{
          fontWeight: 700,
          color: theme.palette.primary.light,
          textShadow: 'none'
        }}
      >
        {value}
      </Typography>
      <Typography variant="body1" sx={{ color: theme.palette.secondary.main, mt: 1 }}>
        {label}
      </Typography>
    </Box>
  );
};

// ÚJ: Statisztika Skeleton Box komponens
const StatSkeletonBox = () => {
  const theme = useTheme();
  return (
    <Box sx={{ textAlign: 'center' }}>
      <Skeleton variant="text" width="50%" height={30} sx={{ mx: 'auto', backgroundColor: theme.palette.secondary.light + '30' }} />
      <Skeleton variant="text" width="70%" height={20} sx={{ mx: 'auto', mt: 1, backgroundColor: theme.palette.secondary.main + '30' }} />
    </Box>
  );
};

// ÚJ: Keyframes a fénylés animációhoz (másolva a Home.js-ből)
const shimmerAnimation = keyframes`
  0% { transform: translateX(-100%) skewX(-30deg); }
  5% { transform: translateX(200%) skewX(-30deg); } /* A fénylés gyorsan lefut az első 5%-ban */
  100% { transform: translateX(200%) skewX(-30deg); } /* A maradék időben a végállapotban marad */
`;

// ÚJ: Styled Button komponens a fényléshez (másolva a Home.js-ből)
const ShimmerButton = styled(Button)(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: `linear-gradient(to right, rgba(255,255,255,0) 0%, rgba(255,255,255,0.3) 50%, rgba(255,255,255,0) 100%)`,
    transform: 'translateX(-100%) skewX(-30deg)',
    animation: `${shimmerAnimation} 20s ease-in-out infinite`, // 20s teljes ciklus, végtelen ismétlődés
  },
}));


// Aktív szűrők száma a "Szűrők" gombon (kis kerek jelvény)
const FilterCountBadge = ({ count, inverted = false }) => {
  const theme = useTheme();
  return (
    <Box
      component="span"
      sx={{
        ml: 1,
        minWidth: 22,
        height: 22,
        px: 0.75,
        borderRadius: '11px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '0.75rem',
        fontWeight: 700,
        bgcolor: inverted ? theme.palette.common.white : theme.palette.primary.dark,
        color: inverted ? theme.palette.primary.dark : theme.palette.common.white,
      }}
    >
      {count}
    </Box>
  );
};

// Szűrőcsoport címe ("Mit és hol?", "Ársáv")
const FilterGroupTitle = ({ children, sx }) => (
  <Typography
    variant="overline"
    component="h3"
    sx={{ display: 'block', color: 'text.secondary', mb: 1.5, lineHeight: 1.5, ...sx }}
  >
    {children}
  </Typography>
);

// Egy oldalon megjelenő szolgáltatók száma (desktop/mobil nézetben; a 2 oszlopos
// tablet nézet a saját, fix 10-es értékét használja a teljes utolsó sor miatt)
const PAGE_SIZE_OPTIONS = [15, 30, 45];

const ServiceProviders = () => {
  const [searchParams] = useSearchParams();
  // SEO landing oldalak: /szolgaltatok/:categorySlug(/:countySlug) és /szolgaltatok/helyszin/:countySlug
  const { categorySlug, countySlug } = useParams();
  const isLandingRoute = Boolean(categorySlug || countySlug);
  const { hash, pathname } = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  // 2 oszlopos (sm) elrendezésnél 10 kártya/oldal, hogy az utolsó sor ne maradjon 1 kártyás
  const isTwoColumn = useMediaQuery(theme.breakpoints.only('sm'), { noSsr: true });
  // Telefonon a részletes szűrők alulról felcsúszó panelben nyílnak (desktopon a kártyán belül)
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });
  const [pageSizeChoice, setPageSizeChoice] = useState(PAGE_SIZE_OPTIONS[0]);
  const pageLimit = isTwoColumn ? 10 : pageSizeChoice;
  const { headerHeight } = useLayout();
  const { isAuthenticated } = useAuth();

  // Állapotok
  const [providers, setProviders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [counties, setCounties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [platformStats, setPlatformStats] = useState(null);
  const [filteredRatingStats, setFilteredRatingStats] = useState(null);

  // Sticky keresősáv
  const heroSearchRef = useRef(null);
  const [showStickySearch, setShowStickySearch] = useState(false);
  const [stickySearchFocused, setStickySearchFocused] = useState(false);

  // Keresési állapotok
  const [searchTerm, setSearchTerm] = useState('');   // Ez triggereli az API hívást
  const [inputValue, setInputValue] = useState('');   // Ez csak a mező értéke gépelés közben
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedCounty, setSelectedCounty] = useState('');
  const [selectedDate, setSelectedDate] = useState(null);
  const [sortBy, setSortBy] = useState('random');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  // Seed a véletlenszerű rendezéshez — mountonként egyszer generálódik, így a sorrend
  // lapozás közben stabil marad, új oldalbetöltésnél viszont újrakeveredik
  const loadReqIdRef = useRef(0);
  const randomSeedRef = useRef(Math.floor(Math.random() * 2 ** 31) || 1);
  const PRICE_MAX = 5000000;
  const PRICE_STEP = 50000;
  const SLIDER_MAX = 1000;
  const scaleToPrice = (v) => Math.round((v / SLIDER_MAX) ** 2 * PRICE_MAX / PRICE_STEP) * PRICE_STEP;
  const priceToScale = (p) => Math.round(Math.sqrt(Math.max(0, p) / PRICE_MAX) * SLIDER_MAX);
  const [priceRange, setPriceRange] = useState([0, PRICE_MAX]);     // committed → API hívás
  const [priceRangeUI, setPriceRangeUI] = useState([0, PRICE_MAX]); // vizuális Ft → input + slider konverzión át

  // Dialog állapotok
  const [offerRequestDialogOpen, setOfferRequestDialogOpen] = useState(false);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [contactType, setContactType] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // Quick View Dialog állapotok
  const [quickViewOpen, setQuickViewOpen] = useState(false);
  const [quickViewProvider, setQuickViewProvider] = useState(null);
  const [quickViewImgIdx, setQuickViewImgIdx] = useState(0);
  const [quickViewLightboxOpen, setQuickViewLightboxOpen] = useState(false);
  const [loginPromptOpen, setLoginPromptOpen] = useState(false);
  const [pendingQuickViewId, setPendingQuickViewId] = useState(null);
  const quickViewTouchStartX = useRef(null);


  // Pagination
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 9,
    total: 0,
    pages: 0
  });

  // Magyar megyék listája
  const hungarianCounties = [
    'Bács-Kiskun',
    'Baranya',
    'Békés',
    'Borsod-Abaúj-Zemplén',
    'Budapest',
    'Csongrád-Csanád',
    'Fejér',
    'Győr-Moson-Sopron',
    'Hajdú-Bihar',
    'Heves',
    'Jász-Nagykun-Szolnok',
    'Komárom-Esztergom',
    'Nógrád',
    'Pest',
    'Somogy',
    'Szabolcs-Szatmár-Bereg',
    'Tolna',
    'Vas',
    'Veszprém',
    'Zala'
  ];

// A loadProviders funkcióban, a response kezelés után
const loadProviders = useCallback(async () => {
  // Elavult válaszok eldobása: a gyors egymás utáni lekérések közül csak a legutolsó számít
  const reqId = ++loadReqIdRef.current;
  try {
    setLoading(true);
    setError(null);

    // Dátum formázás az API-nak (YYYY-MM-DD)
    const dateParam = selectedDate
      ? `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`
      : null;

    // Ha van keresési kifejezés VAGY dátum szűrő → Meilisearch, egyébként Prisma
    if (searchTerm.trim() || dateParam) {
      const qs = new URLSearchParams({
        q: searchTerm.trim(),
        page: currentPage,
        limit: pageLimit,
        ...(selectedCategory && { categoryId: selectedCategory }),
        ...(selectedCity.trim() && { city: selectedCity.trim() }),
        ...(selectedCounty && { county: selectedCounty }),
        ...(dateParam && { date: dateParam }),
      });
      const res = await fetch(`${API_BASE_URL}/search?${qs}`);
      const data = await res.json();
      if (reqId !== loadReqIdRef.current) return;
      let hits = data.hits || [];
      if (priceRange[0] > 0 || priceRange[1] < PRICE_MAX) {
        hits = hits.filter(p =>
          p.priceRangeMin === 0 || p.priceRangeMax === 0 ||
          (p.priceRangeMin <= priceRange[1] && p.priceRangeMax >= priceRange[0])
        );
      }
      // Keresésnél a Meilisearch relevancia-sorrendjét tartjuk ('random' itt nem értelmezett)
      if (sortBy === 'priceRangeMin') {
        hits.sort((a, b) => {
          const nullFallback = sortOrder === 'asc' ? Infinity : -Infinity;
          const aP = a.priceRangeMin ?? nullFallback;
          const bP = b.priceRangeMin ?? nullFallback;
          return sortOrder === 'asc' ? aP - bP : bP - aP;
        });
      }
      setProviders(hits);
      setPagination(prev => ({
        ...prev,
        page: data.page || currentPage,
        total: data.total || 0,
        pages: data.pages || 0,
      }));
      if ((data.pages || 0) > 0 && currentPage > data.pages) {
        setCurrentPage(data.pages);
      }
      // Keresésnél (Meilisearch) nincs szűrt átlagértékelés-aggregátum, a globális platform-átlagra esünk vissza
      setFilteredRatingStats(null);
    } else {
      const params = {
        page: currentPage,
        limit: pageLimit,
        sortBy,
        sortOrder,
        ...(sortBy === 'random' && { seed: randomSeedRef.current }),
        ...(selectedCategory && { category: selectedCategory }),
        ...(selectedCity.trim() && { city: selectedCity.trim() }),
        ...(selectedCounty && { county: selectedCounty }),
        ...(priceRange[0] > 0 && { minPrice: priceRange[0] }),
        ...(priceRange[1] < PRICE_MAX && { maxPrice: priceRange[1] }),
      };

      const response = await providerService.getProviders(params);
      if (reqId !== loadReqIdRef.current) return;

      if (response.success) {
        setProviders(response.data.providers);
        setPagination({
          page: response.data.pagination.page,
          limit: response.data.pagination.limit,
          total: response.data.pagination.total,
          pages: response.data.pagination.pages
        });
        setFilteredRatingStats(response.data.ratingStats || null);
        // Limit-váltás (9↔10) csökkentheti az oldalszámot — ne ragadjunk üres oldalon
        if (response.data.pagination.pages > 0 && currentPage > response.data.pagination.pages) {
          setCurrentPage(response.data.pagination.pages);
        }
      } else {
        throw new Error(response.message || 'Adatok betöltése sikertelen');
      }
    }
  } catch (error) {
    if (reqId !== loadReqIdRef.current) return;
    console.error('Providers loading error:', error);
    setError(error.message);
  } finally {
    if (reqId === loadReqIdRef.current) setLoading(false);
  }
}, [currentPage, searchTerm, selectedCategory, selectedCity, selectedCounty, selectedDate, sortBy, sortOrder, pageLimit, priceRange]);

const loadCategories = useCallback(async () => {
  try {
    const response = await categoryService.getCategories();
    if (response.success) {
      setCategories(response.data.categories);
    } else {
      // Ha az API hívás sikertelen, de van üzenet, azt jelenítsük meg
      setError(response.message || 'Kategóriák betöltése sikertelen.');
      console.error('📂 Categories loading error from API:', response.message);
      setCategories([]); // Üres listát állítunk be, ha hiba van
    }
  } catch (error) {
    console.error('Categories loading error:', error);
    setError('Szerver hiba a kategóriák betöltése során.');
    setCategories([]); // Üres listát állítunk be, ha hiba van
  }
}, []);

  // Scroll-to-top mountkor + platform stats fetch
  useEffect(() => {
    window.scrollTo(0, 0);
    apiService.get('/stats/public')
      .then(res => { if (res.success) setPlatformStats(res.data); })
      .catch(() => {});
  }, []);

  // IntersectionObserver a sticky keresősávhoz
  // loading is a dep: the heroSearchRef Paper only renders after the skeleton early-return exits
  useEffect(() => {
    if (loading) return;
    const observer = new IntersectionObserver(
      ([entry]) => setShowStickySearch(!entry.isIntersecting),
      { threshold: 0, rootMargin: `${-(headerHeight || 64)}px 0px 0px 0px` }
    );
    if (heroSearchRef.current) {
      observer.observe(heroSearchRef.current);
    }
    return () => observer.disconnect();
  }, [headerHeight, loading]);

  // Hash-alapú scroll a kártyákhoz
  useEffect(() => {
    if (hash === '#kártyák' && !loading && providers.length > 0) {
      const el = document.getElementById('kártyák');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [hash, loading, providers]);

  // Adatok betöltése
  useEffect(() => {
    loadProviders();
  }, [loadProviders]);

  // Autocomplete kattintás után: keresési eredmények megérkezésekor Quick View megnyitása
  useEffect(() => {
    if (!pendingQuickViewId || loading || providers.length === 0) return;
    const found = providers.find(p => String(p.id) === String(pendingQuickViewId));
    if (found) {
      setPendingQuickViewId(null);
      setQuickViewProvider(found);
      setQuickViewImgIdx(0);
      setQuickViewLightboxOpen(false);
      setQuickViewOpen(true);
    }
  }, [providers, loading, pendingQuickViewId]);

  // Kategóriák betöltése
  useEffect(() => {
    loadCategories();
    setCounties(hungarianCounties);
  }, [loadCategories]);

  // URL paraméter kezelése
  useEffect(() => {
    const categoryParam = searchParams.get('category');
    const searchParam = searchParams.get('search');
    const cityParam = searchParams.get('city');
    const countyParam = searchParams.get('county');
    const quickViewParam = searchParams.get('quickView');
    if (quickViewParam) setPendingQuickViewId(quickViewParam);

    // Landing oldalon a kategóriát és a megyét az útvonal adja (lásd lent), a query nem írja felül
    if (isLandingRoute) {
      // nincs teendő
    } else if (categoryParam && categories.length > 0) {
      // Név, slug (pl. a főoldali "Fedezz fel többet" link) vagy ID is lehet
      const categoryByName = categories.find(cat => cat.name === categoryParam || cat.slug === categoryParam);
      if (categoryByName) {
        setSelectedCategory(categoryByName.id.toString());
      } else {
        const categoryById = categories.find(cat => cat.id.toString() === categoryParam);
        if (categoryById) {
          setSelectedCategory(categoryById.id.toString());
        } else {
          setSelectedCategory('');
        }
      }
    } else {
      setSelectedCategory('');
    }

    if (searchParam) {
      setSearchTerm(searchParam);
      setInputValue(searchParam);
    } else {
      setSearchTerm('');
      setInputValue('');
    }

    if (cityParam) {
      setSelectedCity(cityParam);
    } else {
      setSelectedCity('');
    }

    // ?county=Pest vagy ?county=borsod-abauj-zemplen; ismeretlen értéket figyelmen kívül hagyunk
    if (!isLandingRoute) {
      setSelectedCounty(countyParam ? (findCountyByParam(countyParam) || '') : '');
    }

    setCurrentPage(1);

  }, [searchParams, categories]); // eslint-disable-line react-hooks/exhaustive-deps

  // Landing útvonal → szűrők. Ismeretlen slug esetén a legközelebbi érvényes oldalra irányítunk.
  useEffect(() => {
    if (!isLandingRoute || categories.length === 0) return;

    const category = categorySlug ? categories.find((c) => c.slug === categorySlug) : null;
    const county = countySlug ? findCountyByParam(countySlug) : null;
    if ((categorySlug && !category) || (countySlug && !county)) {
      navigate(providersLandingPath({ categorySlug: category?.slug, county }), { replace: true });
      return;
    }

    setSelectedCategory(category ? category.id.toString() : '');
    setSelectedCounty(county || '');
    setCurrentPage(1);
  }, [categorySlug, countySlug, categories]); // eslint-disable-line react-hooks/exhaustive-deps

  // A kategória/megye szűrő mindig a megfelelő landing URL-ben jelenik meg (/szolgaltatok/fenykepeszek/pest),
  // a sima listán és a ?category=/?county= linkeken is — így a cím, a canonical és a megosztott link
  // egyezik a látott listával. A többi query (pl. search) megmarad, a category/county kikerül belőle.
  useEffect(() => {
    if (categories.length === 0) return;
    const category = categories.find((c) => c.id.toString() === selectedCategory);
    const target = providersLandingPath({ categorySlug: category?.slug, county: selectedCounty });
    if (target === pathname) return;
    const params = new URLSearchParams(searchParams);
    params.delete('category');
    params.delete('county');
    const query = params.toString();
    navigate({ pathname: target, search: query ? `?${query}` : '', hash }, { replace: true });
  }, [selectedCategory, selectedCounty]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = () => {
    setSearchTerm(inputValue);
    setCurrentPage(1);
  };

  const handleSuggestionSelect = (provider) => {
    setPendingQuickViewId(provider.id);
    setInputValue(provider.businessName || '');
    setSearchTerm(provider.businessName || '');
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setInputValue('');
    setSelectedCategory('');
    setSelectedCity('');
    setSelectedCounty('');
    setSelectedDate(null);
    setSortBy('random');
    setSortOrder('desc');
    setPriceRange([0, PRICE_MAX]);
    setPriceRangeUI([0, PRICE_MAX]);
    setCurrentPage(1);
  };

// MÓDOSÍTVA: handleProviderClick most a quick view-t nyitja meg
const handleProviderClick = (provider) => {
  setQuickViewProvider(provider);
  setQuickViewImgIdx(0);
  setQuickViewLightboxOpen(false);
  setQuickViewOpen(true);
  trackProviderEvent(ANALYTICS_EVENTS.QUICK_VIEW_OPEN, provider.id);
};

// ÚJ: Navigálás a teljes profilra a quick view-ból
const handleViewFullProfile = () => {
  setQuickViewOpen(false);
  window.open(`/szolgaltato/${quickViewProvider.slug || quickViewProvider.id}`, '_blank', 'noopener,noreferrer');
};


  const handleContact = (provider, method, event) => {
    event.stopPropagation();
    if (!isAuthenticated) {
      setLoginPromptOpen(true);
      return;
    }
    setSelectedProvider(provider);
    setContactType(method);
    setContactDialogOpen(true);
  };

  const handleCopyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      setSnackbarMessage(`${type} másolva a vágólapra!`);
      setSnackbarOpen(true);
      setContactDialogOpen(false);
    } catch (error) {
      console.error('Clipboard copy failed:', error);
      setSnackbarMessage('Másolás sikertelen');
      setSnackbarOpen(true);
    }
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBookingClick = (provider, event) => {
    event.stopPropagation();
    if (!isAuthenticated) {
      setLoginPromptOpen(true);
      return;
    }
    setSelectedProvider(provider);
    setOfferRequestDialogOpen(true);
  };

  const handleOfferRequestSuccess = (booking) => {
    console.log('Offer request created:', booking);
    setSnackbarMessage('Az ajánlatkérés sikeresen elküldve! A szolgáltatót értesítettük az ajánlatkérési igényéről.');
    setSnackbarOpen(true);
    setOfferRequestDialogOpen(false);
  };

  // Rendezés egy választóban (korábban külön "Rendezés" + "Sorrend"); érték: `${sortBy}:${sortOrder}`
  const sortOptions = [
    { value: 'random:desc', label: 'Véletlenszerű' },
    { value: 'ratingAverage:desc', label: 'Legjobb értékelés' },
    { value: 'reviewCount:desc', label: 'Legtöbb vélemény' },
    { value: 'profileViews:desc', label: 'Legnépszerűbb' },
    { value: 'createdAt:desc', label: 'Legújabb' },
    { value: 'priceRangeMin:asc', label: 'Ár szerint növekvő' },
    { value: 'priceRangeMin:desc', label: 'Ár szerint csökkenő' },
    { value: 'businessName:asc', label: 'Név szerint (A–Z)' },
  ];
  const sortValue = sortOptions.some(o => o.value === `${sortBy}:${sortOrder}`)
    ? `${sortBy}:${sortOrder}`
    : `${sortBy}:${sortBy === 'businessName' ? 'asc' : 'desc'}`;
  const handleSortChange = (value) => {
    const [by, order] = value.split(':');
    setSortBy(by);
    setSortOrder(order);
    setCurrentPage(1);
  };

  // Aktív (panelben állítható) szűrők száma — a "Szűrők" gombon jelenik meg
  const priceActive = priceRange[0] > 0 || priceRange[1] < PRICE_MAX;
  const activeFilterCount = [selectedCategory, selectedCounty, selectedCity.trim(), selectedDate, priceActive].filter(Boolean).length;
  const hasAnyFilter = activeFilterCount > 0 || !!searchTerm;

  // Ár rövid alakban a csúszka feliratához: 1500000 → "1,5 M", 300000 → "300 e"
  const formatPriceShort = (p) => {
    if (p >= 1000000) return `${(p / 1000000).toLocaleString('hu-HU', { maximumFractionDigits: 1 })} M`;
    if (p >= 1000) return `${Math.round(p / 1000)} e`;
    return String(p);
  };
  const priceLabel = (range) => (range[0] <= 0 && range[1] >= PRICE_MAX)
    ? 'Bármilyen ár'
    : `${formatPriceShort(range[0])} – ${range[1] >= PRICE_MAX ? '5 M+' : formatPriceShort(range[1])} Ft`;

  // Egységes mezőstílus: fehér háttér a bézs kártyán, lekerekítés
  const filterFieldSx = {
    '& .MuiOutlinedInput-root': { borderRadius: '12px', backgroundColor: theme.palette.common.white },
    // Az MUI X DatePicker saját mezőt renderel, más osztálynévvel
    '& .MuiPickersOutlinedInput-root': { borderRadius: '12px', backgroundColor: theme.palette.common.white },
  };

  // Minden választó menüje egyformán: a mező alá nyílik (nem a kijelölt elem köré, ami hosszú
  // listánál az egész oldalt eltakarta), legfeljebb 360px magas, görgethető
  const filterMenuProps = {
    anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
    transformOrigin: { vertical: 'top', horizontal: 'left' },
    PaperProps: { sx: { maxHeight: 360, mt: 0.5, borderRadius: '12px' } },
  };

  const openFilters = () => {
    setFiltersOpen(true);
    if (!isPhone) heroSearchRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const commitPriceUI = (range) => {
    const fixed = [Math.min(range[0], range[1]), Math.max(range[0], range[1])];
    setPriceRangeUI(fixed);
    setPriceRange(fixed);
    setCurrentPage(1);
  };

  const handleCategoryChipClick = (categoryId) => {
    setSelectedCategory(categoryId.toString());
    setCurrentPage(1);
  };

  const handleCityChipClick = (city) => {
    setSelectedCity(city);
    setCurrentPage(1);
  };

  // Loading skeleton komponens
   const ProviderSkeleton = () => (
    <Card sx={{ borderRadius: '20px', height: '100%' }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Skeleton variant="circular" width={50} height={50} sx={{ mr: 1.5 }} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="70%" height={20} />
            <Skeleton variant="text" width="50%" height={16} />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5, mb: 2 }}>
          <Skeleton variant="rounded" width={60} height={24} />
          <Skeleton variant="rounded" width={50} height={24} />
        </Box>
        <Skeleton variant="text" width="100%" height={16} sx={{ mb: 1 }} />
        <Skeleton variant="text" width="80%" height={16} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" width="100%" height={32} sx={{ mb: 1 }} />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Skeleton variant="rectangular" width="48%" height={32} />
          <Skeleton variant="rectangular" width="48%" height={32} />
        </Box>
      </CardContent>
    </Card>
  );

    // Loading state
  if (loading && providers.length === 0) {
    const skeletonStatsData = [{}, {}, {}, {}]; // Placeholders for skeleton stats
    return (
      <> {/* Fragment a teljes szélességű Hero szekcióhoz */}
        {/* Hero szekció skeleton */}
        <Box // Wrapper Box a ragyogás pseudo-elemhez és a margin-bottom-hoz
          sx={{
            position: 'relative',
            borderRadius: '24px', // Illeszkedik a Paper lekerekítéséhez
            mb: 4, // A margin-bottom ide került a Paper-ről
            '&::before': { // Ragyogás pseudo-elem
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              borderRadius: '24px', // Illeszkedik a Paper lekerekítéséhez
              boxShadow: `0px 0px 25px 8px ${theme.palette.secondary.main}40`, // Finom ragyogás (40 = 25% alpha)
              zIndex: -1, // A Paper mögé helyezi
              transition: 'box-shadow 0.3s ease-in-out',
            },
          }}
        >
          <Paper
            elevation={3}
            sx={{
              py: 6,
              px: 4,
              borderRadius: '24px',
              backgroundImage: ROZSAK_BG,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                borderRadius: '24px',
                background: 'rgba(255,255,255,0.1)',
                zIndex: 0,
              }
            }}
          >
            <Container maxWidth="lg"> {/* A tartalom középre igazítása */}
              <Box sx={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
                <Skeleton variant="text" width="60%" height={48} sx={{ mx: 'auto', mb: 2 }} />
                <Skeleton variant="text" width="40%" height={24} sx={{ mx: 'auto' }} />
                {/* Statisztikák skeleton */}
                <Paper elevation={2} sx={{ p: 3, borderRadius: '16px', mt: 4, mx: 'auto', maxWidth: 600 }}>
                  <Grid container spacing={4} justifyContent="center">
                    {skeletonStatsData.map((_, index) => (
                      <Grid
                        item
                        xs={6}
                        sm={3}
                        key={index}
                        sx={{
                          position: 'relative',
                          '&:not(:last-of-type)::after': {
                            content: '""',
                            position: 'absolute',
                            top: '20%',
                            bottom: '20%',
                            right: '-16px', // Half of spacing (spacing={4} is 32px)
                            width: '1px',
                            backgroundColor: theme.palette.divider,
                            display: {
                              xs: (index % 2 === 0 && index < skeletonStatsData.length - 1) ? 'block' : 'none',
                              sm: (index < skeletonStatsData.length - 1) ? 'block' : 'none',
                            },
                          },
                        }}
                      >
                        <StatSkeletonBox />
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              </Box>
            </Container>
          </Paper>
        </Box>

        <Container maxWidth="lg" sx={{ py: 4 }}> {/* A többi tartalom a Container-ben marad */}
          {/* Keresési sáv skeleton */}
          <Paper sx={{ mb: 4, p: 3, borderRadius: '20px' }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Skeleton variant="rectangular" height={56} sx={{ borderRadius: '12px' }} />
              </Grid>
              <Grid item xs={12} md={8}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Skeleton variant="rectangular" height={56} sx={{ flex: 1, borderRadius: '12px' }} />
                  <Skeleton variant="rectangular" height={56} sx={{ flex: 1, borderRadius: '12px' }} />
                  <Skeleton variant="rectangular" width={120} height={56} sx={{ borderRadius: '12px' }} />
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Szolgáltatók skeleton */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
              },
              gap: 3,
              mb: 4
            }}
          >
            {[...Array(pageLimit)].map((_, index) => (
              <ProviderSkeleton key={index} />
            ))}
          </Box>
        </Container>
      </>
    );
  }

  // ÚJ: Statisztikák adatai
  const statsData = [
    { value: <CountUp end={pagination.total} suffix="" />, label: 'Szolgáltató' },
    { value: <CountUp end={categories.length} suffix="" />, label: 'Kategória' },
    { value: <CountUp end={4.9} duration={2000} decimalPlaces={1} />, label: 'Átlag értékelés' },
    { value: <CountUp end={counties.length} suffix="" />, label: 'Megye' }
  ];


  // A részletes szűrők tartalma — desktopon a kártyán belül nyílik, telefonon az alsó panelben
  const filterFields = (
    <Box>
      <FilterGroupTitle>Mit és hol?</FilterGroupTitle>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <FormControl fullWidth sx={filterFieldSx}>
            <InputLabel shrink>Kategória</InputLabel>
            <Select
              value={selectedCategory}
              label="Kategória"
              onChange={(e) => { setSelectedCategory(e.target.value); setCurrentPage(1); }}
              displayEmpty
              notched
              MenuProps={filterMenuProps}
              renderValue={(value) => {
                const cat = categories.find(c => c.id.toString() === value);
                if (!cat) return 'Összes kategória';
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                    {cat.icon && <img src={cat.icon} alt="" style={{ width: 20, height: 20, objectFit: 'contain' }} />}
                    <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{cat.name}</Box>
                  </Box>
                );
              }}
            >
              <MenuItem value="">Összes kategória</MenuItem>
              {categories.map((category) => (
                <MenuItem key={category.id} value={category.id.toString()}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {category.icon && (
                      <img src={category.icon} alt="" style={{ width: 20, height: 20, objectFit: 'contain' }} />
                    )}
                    {category.name}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <FormControl fullWidth sx={filterFieldSx}>
            <InputLabel shrink>Megye</InputLabel>
            <Select
              value={selectedCounty}
              label="Megye"
              onChange={(e) => { setSelectedCounty(e.target.value); setCurrentPage(1); }}
              displayEmpty
              notched
              MenuProps={filterMenuProps}
            >
              <MenuItem value="">Összes megye</MenuItem>
              {counties.map((county) => (
                <MenuItem key={county} value={county}>{county}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <TextField
            fullWidth
            label="Város"
            placeholder="Bármelyik"
            value={selectedCity}
            onChange={(e) => setSelectedCity(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            InputLabelProps={{ shrink: true }}
            sx={filterFieldSx}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={hu}>
            <DatePicker
              label="Esküvő dátuma"
              value={selectedDate}
              onChange={(newDate) => { setSelectedDate(newDate); setCurrentPage(1); }}
              minDate={new Date()}
              slotProps={{
                textField: {
                  fullWidth: true,
                  InputLabelProps: { shrink: true },
                  helperText: undefined,
                  sx: filterFieldSx,
                },
                field: { clearable: true, onClear: () => setSelectedDate(null) },
              }}
            />
          </LocalizationProvider>
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 2, mt: 3 }}>
        <FilterGroupTitle sx={{ mb: 0 }}>Ársáv</FilterGroupTitle>
        <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.primary.dark }}>
          {priceLabel(priceRangeUI)}
        </Typography>
      </Box>
      <Box sx={{ px: 1.5, mt: 1 }}>
        <Slider
          value={[priceToScale(priceRangeUI[0]), priceToScale(priceRangeUI[1])]}
          onChange={(_, v) => setPriceRangeUI([scaleToPrice(v[0]), scaleToPrice(v[1])])}
          onChangeCommitted={(_, v) => { setPriceRange([scaleToPrice(v[0]), scaleToPrice(v[1])]); setCurrentPage(1); }}
          min={0}
          max={SLIDER_MAX}
          step={1}
          disableSwap
          valueLabelDisplay="off"
          getAriaValueText={(v) => `${scaleToPrice(v).toLocaleString('hu-HU')} Ft`}
          marks={[
            { value: 0,                      label: '0' },
            { value: priceToScale(500000),   label: '500 e' },
            { value: priceToScale(1000000),  label: '1 M' },
            { value: priceToScale(2000000),  label: '2 M' },
            { value: priceToScale(3500000),  label: '3,5 M' },
            { value: SLIDER_MAX,             label: '5 M+' },
          ]}
          sx={{
            color: theme.palette.primary.main,
            '& .MuiSlider-markLabel': { fontSize: '0.75rem', color: theme.palette.text.secondary },
          }}
        />
      </Box>
      <Grid container spacing={2} sx={{ mt: 0.5 }}>
        <Grid size={{ xs: 6, md: 3 }}>
          <TextField
            fullWidth
            size="small"
            label="Minimum (Ft)"
            type="number"
            value={priceRangeUI[0] || ''}
            placeholder="0"
            onChange={(e) => {
              if (e.target.value === '') { setPriceRangeUI([0, priceRangeUI[1]]); return; }
              const v = Math.max(0, Math.min(parseInt(e.target.value) || 0, PRICE_MAX));
              setPriceRangeUI([v, priceRangeUI[1]]); // nem clampolunk max-hoz — csak blur-kor
            }}
            onBlur={() => commitPriceUI(priceRangeUI)}
            onKeyDown={(e) => { if (e.key === 'Enter') commitPriceUI(priceRangeUI); }}
            inputProps={{ min: 0, max: PRICE_MAX, step: PRICE_STEP, inputMode: 'numeric' }}
            InputLabelProps={{ shrink: true }}
            sx={filterFieldSx}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <TextField
            fullWidth
            size="small"
            label="Maximum (Ft)"
            type="number"
            value={priceRangeUI[1] >= PRICE_MAX ? '' : priceRangeUI[1]}
            placeholder="Nincs felső"
            onChange={(e) => {
              if (e.target.value === '') { setPriceRangeUI([priceRangeUI[0], PRICE_MAX]); return; }
              const v = Math.max(0, Math.min(parseInt(e.target.value) || 0, PRICE_MAX));
              setPriceRangeUI([priceRangeUI[0], v]); // nem clampolunk min-hez — csak blur-kor
            }}
            onBlur={() => commitPriceUI(priceRangeUI)}
            onKeyDown={(e) => { if (e.key === 'Enter') commitPriceUI(priceRangeUI); }}
            inputProps={{ min: 0, max: PRICE_MAX, step: PRICE_STEP, inputMode: 'numeric' }}
            InputLabelProps={{ shrink: true }}
            sx={filterFieldSx}
          />
        </Grid>
      </Grid>
    </Box>
  );

  // Aktív szűrők chipjei (egy helyen, a szűrőgomb alatt)
  const activeChipSx = { bgcolor: theme.palette.common.white, border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`, color: theme.palette.primary.dark, fontWeight: 500 };
  const activeFilterChips = hasAnyFilter && (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
      {searchTerm && (
        <Chip size="small" label={`„${searchTerm}”`} onDelete={() => { setSearchTerm(''); setInputValue(''); setCurrentPage(1); }} deleteIcon={<Close />} sx={activeChipSx} />
      )}
      {selectedCategory && (
        <Chip size="small" label={categories.find(c => c.id.toString() === selectedCategory)?.name || 'Kategória'} onDelete={() => { setSelectedCategory(''); setCurrentPage(1); }} deleteIcon={<Close />} sx={activeChipSx} />
      )}
      {selectedCounty && (
        <Chip size="small" label={countyLabel(selectedCounty)} onDelete={() => { setSelectedCounty(''); setCurrentPage(1); }} deleteIcon={<Close />} sx={activeChipSx} />
      )}
      {selectedCity.trim() && (
        <Chip size="small" label={selectedCity.trim()} onDelete={() => { setSelectedCity(''); setCurrentPage(1); }} deleteIcon={<Close />} sx={activeChipSx} />
      )}
      {selectedDate && (
        <Chip size="small" label={selectedDate.toLocaleDateString('hu-HU')} onDelete={() => { setSelectedDate(null); setCurrentPage(1); }} deleteIcon={<Close />} sx={activeChipSx} />
      )}
      {priceActive && (
        <Chip size="small" label={priceLabel(priceRange)} onDelete={() => { setPriceRange([0, PRICE_MAX]); setPriceRangeUI([0, PRICE_MAX]); setCurrentPage(1); }} deleteIcon={<Close />} sx={activeChipSx} />
      )}
    </Box>
  );

  // SEO: cím, leírás, H1 és canonical a kiválasztott kategória/megye szerint
  // (a canonical mindig a megfelelő landing URL, a ?category=/?county= variánsoknál is)
  const seoCategory = categories.find((c) => c.id.toString() === selectedCategory) || null;
  const seoCountyLabel = selectedCounty ? countyLabel(selectedCounty) : null;
  const canonicalPath = providersLandingPath({ categorySlug: seoCategory?.slug, county: selectedCounty });
  let seoHeading = 'Találd meg a tökéletes szolgáltatókat';
  let seoTitle = 'Esküvői szolgáltatók | Wedigo';
  let seoDescription = 'Böngéssz esküvői szolgáltatók között! Fotósok, dekoratőrök, zenészek, cateringek és sok más szakember egy helyen. Szűrj kategória és helyszín szerint.';
  if (seoCategory && seoCountyLabel) {
    seoHeading = `${seoCategory.name} – ${seoCountyLabel}`;
    seoTitle = `${seoCategory.name} – ${seoCountyLabel} | Wedigo`;
    seoDescription = `${seoCategory.name} ${seoCountyLabel} területén: böngészd az esküvői szolgáltatók profiljait, értékeléseit és árait a Wedigón.`;
  } else if (seoCategory) {
    seoHeading = seoCategory.name;
    seoTitle = `${seoCategory.name} – esküvői szolgáltatók | Wedigo`;
    seoDescription = `${seoCategory.description ? `${seoCategory.description} ` : ''}${seoCategory.name}: profilok, értékelések és árak egy helyen a Wedigón.`;
  } else if (seoCountyLabel) {
    seoHeading = `Esküvői szolgáltatók – ${seoCountyLabel}`;
    seoTitle = `Esküvői szolgáltatók – ${seoCountyLabel} | Wedigo`;
    seoDescription = `Esküvői szolgáltatók ${seoCountyLabel} területén: fotósok, helyszínek, zenekarok és más szakemberek egy helyen a Wedigón.`;
  }
  // Üres kategória/megye oldal ne kerüljön az indexbe (a linkjeit azért kövesse a kereső)
  const seoNoIndex = Boolean(seoCategory || selectedCounty) && !loading && pagination.total === 0;

  return (
    <>
    {/* Telefon: részletes szűrők alulról felcsúszó panelben, alul élő találatszámmal */}
    <Drawer
      anchor="bottom"
      open={isPhone && filtersOpen}
      onClose={() => setFiltersOpen(false)}
      PaperProps={{
        sx: {
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          maxHeight: '88vh',
          bgcolor: theme.palette.secondary.light,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, pt: 1.5, pb: 1, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Typography variant="h6">Szűrők</Typography>
        <IconButton aria-label="Szűrők bezárása" onClick={() => setFiltersOpen(false)} sx={{ width: 40, height: 40 }}>
          <Close />
        </IconButton>
      </Box>
      <Box sx={{ overflowY: 'auto', px: 2, py: 2, flex: 1 }}>
        {filterFields}
      </Box>
      <Box sx={{ display: 'flex', gap: 1, px: 2, py: 1.5, borderTop: `1px solid ${theme.palette.divider}`, bgcolor: theme.palette.secondary.light }}>
        <Button onClick={handleClearFilters} disabled={!hasAnyFilter} sx={{ minHeight: 48 }}>
          Törlés
        </Button>
        <Button
          variant="contained"
          fullWidth
          onClick={() => setFiltersOpen(false)}
          sx={{ minHeight: 48 }}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : `Mutasd a ${pagination.total} találatot`}
        </Button>
      </Box>
    </Drawer>
    {/* Sticky keresősáv – megjelenik, ha a hero kereső kigörgetett */}
    <Slide direction="down" in={showStickySearch} mountOnEnter unmountOnExit>
      <Paper
        elevation={4}
        sx={{
          position: 'fixed',
          top: headerHeight || 64,
          left: 0,
          right: 0,
          zIndex: 1100,
          py: 1,
          px: 2,
          borderRadius: 0,
          borderBottom: `1px solid ${theme.palette.divider}`,
          background: theme.palette.secondary.light,
          backdropFilter: 'blur(10px)',
        }}
      >
        <Container maxWidth="lg">
          <Stack direction="row" spacing={1} alignItems="center">
            {/* Wrapper capture-eventekkel, hogy ne törjük meg a SearchAutocomplete belső fókusz-kezelőit */}
            <Box
              sx={{ flex: 1 }}
              onFocusCapture={() => setStickySearchFocused(true)}
              onBlurCapture={() => setStickySearchFocused(false)}
            >
              <SearchAutocomplete
                fullWidth
                placeholder="Keresés..."
                value={inputValue}
                onChange={setInputValue}
                onSearch={handleSearch}
                onSuggestionSelect={handleSuggestionSelect}
                iconColor="text.secondary"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    backgroundColor: 'white',
                    height: 44,
                  }
                }}
              />
            </Box>

            {/* Mobilon csak fókusz közben látható; desktopon mindig */}
            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={loading}
              sx={{
                height: 44,
                minWidth: 100,
                borderRadius: '12px',
                flexShrink: 0,
                display: { xs: stickySearchFocused ? 'flex' : 'none', sm: 'flex' },
                background: `linear-gradient(45deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                '&:hover': {
                  background: `linear-gradient(45deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                }
              }}
            >
              Keresés
            </Button>

            <Button
              variant="outlined"
              startIcon={<FilterList sx={{ display: { xs: 'none', sm: 'block' } }} />}
              onClick={openFilters}
              aria-label={activeFilterCount ? `Szűrők (${activeFilterCount} aktív)` : 'Szűrők'}
              sx={{
                height: 44,
                borderRadius: '12px',
                flexShrink: 0,
                borderWidth: 2,
                borderColor: theme.palette.primary.dark,
                color: theme.palette.primary.dark,
                minWidth: { xs: 44, sm: 'auto' },
                px: { xs: 0, sm: 2 },
                whiteSpace: 'nowrap',
                '&:hover': {
                  borderWidth: 2,
                  borderColor: theme.palette.primary.dark,
                  background: `${theme.palette.primary.dark}08`,
                }
              }}
            >
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Szűrők</Box>
              <FilterList sx={{ display: { xs: 'block', sm: 'none' }, fontSize: 20 }} />
              {activeFilterCount > 0 && <FilterCountBadge count={activeFilterCount} />}
            </Button>
          </Stack>
        </Container>
      </Paper>
    </Slide>
    <Helmet>
      <title>{seoTitle}</title>
      <meta name="description" content={seoDescription} />
      <meta property="og:title" content={seoTitle} />
      <meta property="og:description" content={seoDescription} />
      <meta property="og:url" content={`https://wedigo.hu${canonicalPath}`} />
      <link rel="canonical" href={`https://wedigo.hu${canonicalPath}`} />
      {seoNoIndex && <meta name="robots" content="noindex, follow" />}
    </Helmet>
    <Box // ÚJ WRAPPER - Fix háttérhez
      sx={{
        minHeight: '100vh',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: {
            xs: LEAVES_BG_XS,
            md: LEAVES_BG_MD
          },
          backgroundSize: {
            xs: '100% auto',
            md: 'cover'
          },
          backgroundPosition: {
            xs: 'center top',
            md: 'center center'
          },
          backgroundRepeat: 'no-repeat',
          zIndex: -1,
          willChange: 'transform',
          transform: 'translateZ(0)',
        }
      }}
    >
      {/* ÚJ: Egyesített Hero + Keresés szekció */}
      <Box sx={{ py: { xs: 4, md: 8 } }}> {/* MÓDOSÍTVA: px: 2 eltávolítva */}
        <Container maxWidth="lg">
          <Paper
            ref={heroSearchRef}
            elevation={6}
            sx={{
              p: { xs: 3, md: 5 },
              borderRadius: '24px',
              background: theme.palette.secondary.light,
              backdropFilter: 'blur(10px)',
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            {/* Cím */}
            <Typography
              variant="h3"
              component="h1"
              sx={{
                fontFamily: 'Playfair Display',
                fontWeight: 700,
                mb: seoCategory?.description ? 1.5 : 4,
                textAlign: 'center',
                color: theme.palette.primary.dark,
                fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' }
              }}
            >
              {seoHeading}
            </Typography>
            {seoCategory?.description && (
              <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', mb: 4, maxWidth: 640, mx: 'auto' }}>
                {seoCategory.description}
              </Typography>
            )}

            {/* Keresősáv */}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              sx={{ mb: 2 }}
            >
              <SearchAutocomplete
                fullWidth
                placeholder="Keresés"
                value={inputValue}
                onChange={setInputValue}
                onSearch={handleSearch}
                onSuggestionSelect={handleSuggestionSelect}
                iconColor="text.secondary"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    overflow: 'hidden',
                    backgroundColor: theme.palette.secondary.light,
                    height: 56,
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderRadius: '12px',
                  },
                }}
              />
              <ShimmerButton
                variant="contained"
                size="large"
                onClick={handleSearch}
                disabled={loading}
                sx={{
                  minWidth: { xs: '100%', sm: 140 },
                  height: 56,
                  borderRadius: '12px',
                  background: `linear-gradient(45deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 4px 10px ${theme.palette.primary.main}80`,
                    background: `linear-gradient(45deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                  }
                }}
              >
                Keresés
              </ShimmerButton>
            </Stack>

            {/* Szűrő-gombsor: számláló + törlés (csak aktív szűrőnél) */}
            <Stack
              direction="row"
              sx={{ mb: 2, justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}
            >
              <Button
                variant={filtersOpen && !isPhone ? 'contained' : 'outlined'}
                onClick={() => (filtersOpen && !isPhone ? setFiltersOpen(false) : openFilters())}
                startIcon={<FilterList />}
                aria-expanded={filtersOpen}
                sx={{
                  borderRadius: '12px',
                  borderWidth: 2,
                  borderColor: theme.palette.primary.dark,
                  color: filtersOpen && !isPhone ? theme.palette.common.white : theme.palette.primary.dark,
                  bgcolor: filtersOpen && !isPhone ? theme.palette.primary.dark : 'transparent',
                  minHeight: 44,
                  px: 2.5,
                  '&:hover': {
                    borderWidth: 2,
                    bgcolor: filtersOpen && !isPhone ? theme.palette.primary.main : alpha(theme.palette.primary.dark, 0.06),
                  }
                }}
              >
                Szűrők
                {activeFilterCount > 0 && <FilterCountBadge count={activeFilterCount} inverted={filtersOpen && !isPhone} />}
              </Button>
              {hasAnyFilter && (
                <Button
                  onClick={handleClearFilters}
                  disabled={loading}
                  startIcon={<Clear />}
                  sx={{ borderRadius: '12px', minHeight: 44, color: theme.palette.text.secondary }}
                >
                  Szűrők törlése
                </Button>
              )}
            </Stack>

            {activeFilterChips && <Box sx={{ mb: 2 }}>{activeFilterChips}</Box>}

            {/* Részletes szűrők (desktop / tablet: a kártyán belül nyílik) */}
            <Collapse in={filtersOpen && !isPhone} timeout={250} unmountOnExit>
              <Box
                sx={{
                  mb: 3,
                  p: { sm: 2.5, md: 3 },
                  borderRadius: '16px',
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                {filterFields}
              </Box>
            </Collapse>

            {/* Statisztikák - Egy sorban */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: { xs: 2, sm: 3, md: 4 },
                pt: 3,
                borderTop: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Business sx={{ color: theme.palette.primary.main, fontSize: 24 }} />
                <Typography variant="body1" sx={{ fontWeight: 600, color: theme.palette.primary.dark }}>
                  <CountUp end={pagination.total} suffix="+" /> Szolgáltató
                </Typography>
              </Box>
              
              <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FilterList sx={{ color: theme.palette.primary.main, fontSize: 24 }} />
                <Typography variant="body1" sx={{ fontWeight: 600, color: theme.palette.primary.dark }}>
                  <CountUp end={platformStats?.categoryCount ?? categories.length} /> Kategória
                </Typography>
              </Box>
              
              {(filteredRatingStats?.average ?? platformStats?.averageRating) != null && (
                <>
                  <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <StarIcon sx={{ color: theme.palette.secondary.main, fontSize: 24 }} />
                    <Typography variant="body1" sx={{ fontWeight: 600, color: theme.palette.primary.dark }}>
                      <CountUp end={filteredRatingStats?.average ?? platformStats.averageRating} duration={2000} decimalPlaces={1} /> Átlag
                    </Typography>
                  </Box>
                </>
              )}

              <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationOn sx={{ color: theme.palette.primary.main, fontSize: 24 }} />
                <Typography variant="body1" sx={{ fontWeight: 600, color: theme.palette.primary.dark }}>
                  <CountUp end={platformStats?.countyCount ?? counties.length} /> Megye
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Hiba megjelenítése */}
        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 3,
              borderRadius: '12px',
              '& .MuiAlert-icon': { color: theme.palette.error.main }
            }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {/* Találatok száma + rendezés */}
        <Box
          sx={{
            mb: 3,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', sm: 'center' },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                <CountUp end={pagination.total} suffix=" szolgáltató találat" />
              </Typography>
              {pagination.pages > 1 && (
                <Typography variant="body2" color="text.secondary">
                  {currentPage}. oldal a {pagination.pages}-ból
                </Typography>
              )}
            </Box>
            {loading && providers.length > 0 && (
              <CircularProgress size={20} sx={{ color: theme.palette.primary.main }} aria-label="Frissítés" />
            )}
          </Box>

          <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: { xs: 0, sm: 220 }, flex: { xs: 1, sm: 'none' }, ...filterFieldSx }}>
              <InputLabel id="sort-label">Rendezés</InputLabel>
              <Select
                labelId="sort-label"
                MenuProps={filterMenuProps}
                value={sortValue}
                label="Rendezés"
                onChange={(e) => handleSortChange(e.target.value)}
                startAdornment={<Sort fontSize="small" sx={{ color: 'text.secondary', mr: 1 }} />}
              >
                {sortOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {!isTwoColumn && (
              <FormControl size="small" sx={{ minWidth: 110, ...filterFieldSx }}>
                <InputLabel id="page-size-label">Oldalanként</InputLabel>
                <Select
                  labelId="page-size-label"
                  MenuProps={filterMenuProps}
                  value={pageSizeChoice}
                  label="Oldalanként"
                  onChange={(e) => { setPageSizeChoice(Number(e.target.value)); setCurrentPage(1); }}
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <MenuItem key={size} value={size}>{size}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Stack>
        </Box>

        {/* Szolgáltatók listája - CSS GRID */}
        <Box
          id="kártyák"
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
            },
            gap: 3,
            mb: 4
          }}
        >
          {providers.map((provider, index) => (
            <ProviderCard
              key={provider.id}
              provider={provider}
              animationDelay={Math.min(index, 5) * 100}
              onCategoryClick={handleCategoryChipClick}
              onCityClick={handleCityChipClick}
              onCardClick={handleProviderClick}
              listPosition={index + 1}
            />
          ))}
        </Box>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: '20px',
                border: `1px solid ${theme.palette.divider}`
              }}
            >
              <Pagination
                count={pagination.pages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
                sx={{
                  '& .MuiPaginationItem-root': {
                    borderRadius: '12px',
                    fontWeight: 500,
                    '&.Mui-selected': {
                      background: `linear-gradient(45deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                      color: 'white',
                      '&:hover': {
                        background: `linear-gradient(45deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                      }
                    }
                  }
                }}
              />
            </Paper>
          </Box>
        )}

        {/* Üres eredmény */}
        {providers.length === 0 && !loading && (
          <Paper
            elevation={0}
            sx={{
              textAlign: 'center',
              py: 8,
              borderRadius: '20px',
              background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
              border: `1px solid ${theme.palette.divider}`
            }}
          >
            <Box sx={{ mb: 3 }}>
              <Search sx={{ fontSize: 64, color: theme.palette.grey[300] }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
              Nincs találat
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 400, mx: 'auto' }}>
              Nem találtunk szolgáltatót a megadott keresési feltételeknek megfelelően.
              Próbálja meg módosítani a szűrőket.
            </Typography>
            <Stack direction="row" spacing={2} justifyContent="center">
              <Button
                variant="outlined"
                onClick={handleClearFilters}
                sx={{
                  borderRadius: '12px',
                  borderWidth: 2,
                  '&:hover': { borderWidth: 2 },
                  borderColor: theme.palette.primary.main,
                  color: theme.palette.primary.main
                }}
              >
                Szűrők törlése
              </Button>
              <Button
                variant="contained"
                onClick={() => navigate('/')}
                sx={{
                  borderRadius: '10px',
                  background: `linear-gradient(45deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`
                }}
              >
                Vissza a főoldalra
              </Button>
            </Stack>
          </Paper>
        )}

        {/* Kategória landing szöveg + GYIK (SEO), a lista alatt */}
        {seoCategory && <CategorySeoContent category={seoCategory} county={selectedCounty || null} />}

        {/* Kapcsolat Dialog */}
        <Dialog
          open={contactDialogOpen}
          onClose={() => setContactDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ p: 0 }}>
            <DialogHeader
              icon={contactType === 'phone' ? <Phone /> : <Email />}
              title={contactType === 'phone' ? 'Telefonszám' : 'Email cím'}
              onClose={() => setContactDialogOpen(false)}
            />
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              p: 2,
              backgroundColor: theme.palette.grey[100],
              borderRadius: '12px',
              mb: 2
            }}>
              {contactType === 'phone' ? <Phone /> : <Email />}
              <Typography variant="h6" sx={{ flex: 1 }}>
                {contactType === 'phone'
                  ? selectedProvider?.user?.phone
                  : selectedProvider?.user?.email
                }
              </Typography>
              <Button
                variant="contained"
                startIcon={<ContentCopy />}
                onClick={() => handleCopyToClipboard(
                  contactType === 'phone'
                    ? selectedProvider?.user?.phone
                    : selectedProvider?.user?.email,
                  contactType === 'phone' ? 'Telefonszám' : 'Email cím'
                )}
                sx={{
                  borderRadius: '8px',
                  background: `linear-gradient(45deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`
                }}
              >
                Másolás
              </Button>
            </Box>
            <Typography variant="body2" color="text.secondary">
              {contactType === 'phone'
                ? 'Kattintson a másolás gombra a telefonszám vágólapra másolásához.'
                : 'Kattintson a másolás gombra az email cím vágólapra másolásához.'
              }
            </Typography>
          </DialogContent>
        </Dialog>

        {/* OfferRequestForm Dialog */}
        <OfferRequestForm
          open={offerRequestDialogOpen}
          onClose={() => setOfferRequestDialogOpen(false)}
          provider={selectedProvider}
          onSuccess={handleOfferRequestSuccess}
        />

        {/* Snackbar */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={() => setSnackbarOpen(false)}
          message={snackbarMessage}
        />

        {/* Login szükséges dialógus (ajánlatkéréshez) */}
        <Dialog
          open={loginPromptOpen}
          onClose={() => setLoginPromptOpen(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: { borderRadius: '20px', p: 1 } }}
        >
          <DialogTitle sx={{ fontFamily: 'Playfair Display', fontWeight: 700 }}>
            Bejelentkezés szükséges
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary">
              Az ajánlatkéréshez be kell jelentkeznie. Kérjük, jelentkezzen be vagy regisztráljon!
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <Button
              onClick={() => setLoginPromptOpen(false)}
              variant="outlined"
              sx={{ borderRadius: '10px', borderWidth: 2, '&:hover': { borderWidth: 2 } }}
            >
              Mégse
            </Button>
            <Button
              variant="contained"
              onClick={() => navigate('/login', { state: { from: pathname } })}
              sx={{ borderRadius: '10px' }}
            >
              Bejelentkezés
            </Button>
          </DialogActions>
        </Dialog>

        {/* Quick View Dialog – kártya stílusú */}
        {quickViewProvider && (() => {
          const qv = quickViewProvider;
          const getFeaturedStyle = () => {
            if (!qv.featured) return {};
            const colors = {
              TOP:   { border: theme.palette.secondary.main, glow: alpha(theme.palette.secondary.main, 0.25) },
              TIER2: { border: theme.palette.primary.main,   glow: alpha(theme.palette.primary.main, 0.25) },
              TIER3: { border: theme.palette.primary.light,  glow: alpha(theme.palette.primary.light, 0.25) },
            };
            return colors[qv.featured.tier] || {};
          };
          const fs = getFeaturedStyle();
          return (
            <Dialog
              open={quickViewOpen}
              onClose={() => setQuickViewOpen(false)}
              maxWidth="md"
              fullWidth
              PaperProps={{
                sx: {
                  borderRadius: '20px',
                  overflow: 'hidden',
                  // Mobilon a tartalom (kép + adatok + leírás + gombok) magasabb
                  // lehet, mint a képernyő — a Paper-t korlátozott magasságúra és
                  // flex-konténerré tesszük, hogy a belső doboz görgethető legyen
                  // helyette, ne csak levágódjon a gombsor a Paper alján.
                  display: 'flex',
                  flexDirection: 'column',
                  maxHeight: 'calc(100dvh - 32px)',
                  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
                  border: qv.featured ? `2px solid ${fs.border}` : `1px solid ${theme.palette.divider}`,
                  boxShadow: qv.featured
                    ? `0 8px 40px ${fs.glow}, 0 2px 12px rgba(0,0,0,0.12)`
                    : '0 8px 40px rgba(0,0,0,0.18)',
                },
              }}
            >
              {/* Lebegő bezáró gomb */}
              <IconButton
                onClick={() => setQuickViewOpen(false)}
                size="small"
                sx={{
                  position: 'absolute',
                  top: 12,
                  right: 12,
                  zIndex: 10,
                  bgcolor: alpha(theme.palette.background.paper, 0.85),
                  backdropFilter: 'blur(4px)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  '&:hover': { bgcolor: theme.palette.background.paper, transform: 'scale(1.1)' },
                  transition: 'all 0.2s',
                }}
              >
                <Close fontSize="small" />
              </IconButton>

              {/* Két oszlopos layout: kép bal, info jobb.
                  Mobilon (oszlop elrendezés) az egész doboz görgethető, hogy a
                  kép alatti adatok és gombok mindig elérhetők legyenek, még ha
                  a Paper korlátozott magassága miatt nem fér is ki egyszerre. */}
              <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                minHeight: { xs: 0, sm: 480 },
                flex: '1 1 auto',
                overflowY: { xs: 'auto', sm: 'visible' },
              }}>

                {/* Bal: Kép (inline slider, objectFit: cover) */}
                <Box sx={{
                  width: { xs: '100%', sm: '55%' },
                  flexShrink: 0,
                  position: 'relative',
                  minHeight: { xs: 240, sm: 480 },
                  overflow: 'hidden',
                  bgcolor: theme.palette.grey[200],
                  alignSelf: 'stretch',
                }}>
                  {(() => {
                    const imgs = qv.images?.length > 0 ? qv.images : [{ imageUrl: '/images/default-provider.jpg', id: 'default' }];
                    const idx = Math.min(quickViewImgIdx, imgs.length - 1);
                    const goPrev = () => setQuickViewImgIdx(i => {
                      if (i <= 0) return i;
                      trackGallerySwipe(qv.id, 'prev');
                      return i - 1;
                    });
                    const goNext = () => setQuickViewImgIdx(i => {
                      if (i >= imgs.length - 1) return i;
                      trackGallerySwipe(qv.id, 'next');
                      return i + 1;
                    });
                    const handleTouchStart = (e) => {
                      quickViewTouchStartX.current = e.touches[0].clientX;
                    };
                    const handleTouchEnd = (e) => {
                      if (quickViewTouchStartX.current === null) return;
                      const delta = e.changedTouches[0].clientX - quickViewTouchStartX.current;
                      quickViewTouchStartX.current = null;
                      if (Math.abs(delta) < 50) return;
                      if (delta < 0) goNext();
                      else goPrev();
                    };
                    return (
                      <>
                        <Box
                          onTouchStart={imgs.length > 1 ? handleTouchStart : undefined}
                          onTouchEnd={imgs.length > 1 ? handleTouchEnd : undefined}
                          sx={{
                            position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                            cursor: 'zoom-in',
                            '&:hover .qv-zoom-hint': { opacity: 1 },
                          }}
                          onClick={() => setQuickViewLightboxOpen(true)}
                        >
                          <img
                            src={imgs[idx].imageUrl}
                            alt={qv.businessName}
                            style={{
                              width: '100%', height: '100%',
                              objectFit: 'cover', objectPosition: 'center',
                            }}
                          />
                          <Box
                            className="qv-zoom-hint"
                            sx={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              bgcolor: 'rgba(0,0,0,0.45)',
                              borderRadius: '50%',
                              width: 36,
                              height: 36,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              opacity: 0,
                              transition: 'opacity 0.2s',
                              pointerEvents: 'none',
                              zIndex: 1,
                            }}
                          >
                            <ZoomIn sx={{ color: 'white', fontSize: 20 }} />
                          </Box>
                        </Box>
                        {imgs.length > 1 && (
                          <>
                            {idx > 0 && (
                              <IconButton
                                onClick={goPrev}
                                sx={{
                                  position: 'absolute', top: '50%', left: 8,
                                  transform: 'translateY(-50%)',
                                  bgcolor: 'rgba(0,0,0,0.5)', color: 'white',
                                  '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                                  zIndex: 2,
                                }}
                              >
                                <ArrowBackIos sx={{ fontSize: 18, position: 'relative', left: 3 }} />
                              </IconButton>
                            )}
                            {idx < imgs.length - 1 && (
                              <IconButton
                                onClick={goNext}
                                sx={{
                                  position: 'absolute', top: '50%', right: 8,
                                  transform: 'translateY(-50%)',
                                  bgcolor: 'rgba(0,0,0,0.5)', color: 'white',
                                  '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                                  zIndex: 2,
                                }}
                              >
                                <ArrowForwardIos sx={{ fontSize: 18 }} />
                              </IconButton>
                            )}
                            {/* Lap-pöttyök – aktuális kép jelzése */}
                            <Box
                              sx={{
                                position: 'absolute',
                                left: '50%',
                                bottom: 10,
                                transform: 'translateX(-50%)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.75,
                                zIndex: 2,
                              }}
                            >
                              {imgs.map((image, dotIdx) => (
                                <Box
                                  key={`qv-dot-${image.id ?? dotIdx}`}
                                  onClick={(e) => { e.stopPropagation(); setQuickViewImgIdx(dotIdx); }}
                                  sx={{
                                    width: dotIdx === idx ? 7 : 6,
                                    height: dotIdx === idx ? 7 : 6,
                                    borderRadius: '50%',
                                    cursor: 'pointer',
                                    bgcolor: dotIdx === idx ? '#ffffff' : 'rgba(255,255,255,0.5)',
                                    boxShadow: '0 0 3px rgba(0,0,0,0.6)',
                                    transition: 'background-color 0.2s ease, width 0.2s ease, height 0.2s ease',
                                  }}
                                />
                              ))}
                            </Box>
                          </>
                        )}
                      </>
                    );
                  })()}
                  {/* Gradient overlay alul */}
                  <Box sx={{
                    position: 'absolute',
                    bottom: 0, left: 0, right: 0,
                    height: 80,
                    background: 'linear-gradient(to top, rgba(0,0,0,0.35), transparent)',
                    pointerEvents: 'none',
                    zIndex: 1,
                  }} />
                  {/* Ár badge - mindig ugyanott, bal alul (független attól, hogy van-e felirat) */}
                  {qv.priceRangeMin && qv.priceRangeMax && (
                    <Box sx={{
                      position: 'absolute',
                      bottom: 12, left: 12, zIndex: 2,
                      bgcolor: alpha(theme.palette.background.paper, 0.92),
                      backdropFilter: 'blur(6px)',
                      borderRadius: '10px',
                      px: 1.5, py: 0.5,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: theme.palette.primary.dark, fontSize: '0.8rem' }}>
                         {qv.priceRangeMin.toLocaleString()} – {qv.priceRangeMax.toLocaleString()} Ft
                      </Typography>
                    </Box>
                  )}
                  {/* Kép felirata (pl. forrás megjelölés) - rögzített helyen az ár badge fölött, nem tolja el azt */}
                  {(() => {
                    const imgs = qv.images?.length > 0 ? qv.images : [{ imageUrl: '/images/default-provider.jpg', id: 'default' }];
                    const idx = Math.min(quickViewImgIdx, imgs.length - 1);
                    const currentDescription = imgs[idx]?.description;
                    if (!currentDescription) return null;
                    return (
                      <Typography
                        variant="caption"
                        sx={{
                          position: 'absolute',
                          bottom: 46, left: 12, right: 12, zIndex: 2,
                          color: 'white',
                          fontStyle: 'italic',
                          fontSize: '0.7rem',
                          textShadow: '0 1px 2px rgba(0,0,0,0.6)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {currentDescription}
                      </Typography>
                    );
                  })()}
                </Box>

                {/* Jobb: Infó panel.
                    Mobilon a görgetést a szülő (kép+infó) doboz adja az egész
                    oszlophoz, ezért itt nincs külön maxHeight/overflow — egy
                    beágyazott görgethető terület csak összezavarná az érintős
                    scrollozást. Desktopon (sm+) marad a saját, fix magasságú,
                    önállóan görgethető panel a képhez igazítva. */}
                <Box sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  p: { xs: 2.5, sm: 3 },
                  overflowY: { xs: 'visible', sm: 'auto' },
                  maxHeight: { sm: 480 },
                }}>
                  {/* Avatar + név + rating + kedvenc */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
                      <Avatar
                        src={qv.user?.avatarUrl || undefined}
                        sx={{
                          width: 48, height: 48,
                          bgcolor: theme.palette.primary.main,
                          border: `2px solid ${theme.palette.primary.main}`,
                          boxShadow: `0 2px 8px ${theme.palette.primary.main}4D`,
                          flexShrink: 0,
                        }}
                      >
                        {!qv.user?.avatarUrl && (qv.user?.firstName?.charAt(0) || qv.businessName?.charAt(0))}
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.primary.dark, lineHeight: 1.2, mb: 0.3 }}>
                          {qv.businessName}
                        </Typography>
                        {qv.ratingAverage > 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Rating
                              value={parseFloat(qv.ratingAverage)}
                              precision={0.1}
                              readOnly
                              size="small"
                              sx={{
                                '& .MuiRating-iconFilled': { color: theme.palette.secondary.main },
                              }}
                            />
                            <Typography variant="caption" sx={{ color: theme.palette.primary.dark }}>
                              ({qv.reviewCount})
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                    <Box onClick={(e) => e.stopPropagation()} sx={{ flexShrink: 0, mr: { sm: 3 } }}>
                      <FavoriteButton providerId={qv.id} size="small" showTooltip={true} />
                    </Box>
                  </Box>

                  {/* Chipek */}
                  <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5, mb: 2, maxHeight: 56, overflow: 'hidden' }}>
                    {qv.featured && (
                      <FeaturedBadge featured={qv.featured} size="small" variant="chip" />
                    )}
                    {(qv.providerCategories?.length > 0 || qv.category) && (
                      (qv.providerCategories?.length > 0
                        ? qv.providerCategories.map(pc => pc.category)
                        : [qv.category]
                      ).map(cat => (
                        <Chip
                          key={cat.id}
                          avatar={cat.icon ? <Avatar src={cat.icon} sx={{ width: 18, height: 18 }} /> : undefined}
                          label={cat.name}
                          size="small"
                          onClick={() => { handleCategoryChipClick(cat.id); setQuickViewOpen(false); }}
                          sx={{ bgcolor: theme.palette.primary.light, color: theme.palette.primary.dark, fontWeight: 500, fontSize: '0.7rem', height: 24, cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
                        />
                      ))
                    )}
                    {qv.county && (
                      <Chip
                        icon={<LocationOn sx={{ fontSize: 13, color: theme.palette.primary.main }} />}
                        label={qv.county}
                        size="small"
                        variant="outlined"
                        onClick={() => { setSelectedCounty(qv.county); setCurrentPage(1); setQuickViewOpen(false); }}
                        sx={{ borderColor: theme.palette.primary.main, color: theme.palette.primary.dark, fontSize: '0.7rem', height: 24, cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
                      />
                    )}
                    {qv.city && (
                      <Chip
                        label={qv.city}
                        size="small"
                        variant="outlined"
                        onClick={() => { handleCityChipClick(qv.city); setQuickViewOpen(false); }}
                        sx={{ borderColor: theme.palette.divider, color: theme.palette.primary.dark, fontSize: '0.7rem', height: 24, cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
                      />
                    )}
                    {qv.status === 'APPROVED' && (
                      <Chip
                        icon={<Verified sx={{ fontSize: 13 }} />}
                        label="Ellenőrzött"
                        size="small"
                        color="success"
                        sx={{ fontSize: '0.7rem', height: 24 }}
                      />
                    )}
                  </Stack>

                  {/* Leírás */}
                  {qv.shortDescription ? (
                    <Typography variant="body2" sx={{
                      color: theme.palette.primary.dark,
                      lineHeight: 1.6,
                      mb: 2,
                      display: '-webkit-box',
                      WebkitLineClamp: 5,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}>
                      {qv.shortDescription}
                    </Typography>
                  ) : (
                    <Typography variant="body2" sx={{
                      color: theme.palette.primary.dark,
                      lineHeight: 1.6,
                      mb: 2,
                      display: '-webkit-box',
                      WebkitLineClamp: 6,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}>
                      {qv.description || 'Nincs leírás megadva.'}
                    </Typography>
                  )}

                  {/* Elválasztó */}
                  <Box sx={{ borderTop: `1px solid ${theme.palette.divider}`, pt: 2, mt: 'auto' }}>
                    {/* Fő akció gombok */}
                    <Stack direction={{ xs: 'row', sm: 'column' }} spacing={1}>
                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<Visibility />}
                        onClick={() => handleViewFullProfile()}
                        sx={{
                          borderRadius: '10px',
                          background: `linear-gradient(45deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                          py: 1,
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: `0 4px 12px ${theme.palette.primary.main}80`,
                            background: `linear-gradient(45deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                          },
                          transition: 'all 0.2s ease-in-out',
                        }}
                      >
                        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Teljes profil megtekintése</Box>
                        <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Részletek</Box>
                      </Button>
                      <Button
                        fullWidth
                        variant="contained"
                        color="secondary"
                        startIcon={<CalendarToday />}
                        onClick={(e) => handleBookingClick(qv, e)}
                        sx={{
                          borderRadius: '10px',
                          py: 1,
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: `0 4px 12px ${theme.palette.secondary.main}80`,
                          },
                          transition: 'all 0.2s ease-in-out',
                        }}
                      >
                        Ajánlatkérés
                      </Button>
                    </Stack>
                  </Box>
                </Box>
              </Box>

              {/* Nagyméretű képnézegető (lightbox) */}
              <ImageLightbox
                open={quickViewLightboxOpen}
                images={qv.images?.length > 0 ? qv.images : [{ imageUrl: '/images/default-provider.jpg', id: 'default' }]}
                startIndex={Math.min(quickViewImgIdx, (qv.images?.length || 1) - 1)}
                onClose={() => setQuickViewLightboxOpen(false)}
                providerId={qv.id}
              />
            </Dialog>
          );
        })()}
      </Container>
    </Box>
    </>
  );
};

export default ServiceProviders;                